import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import httpClient from "../../utils/httpClient";

const PROVISION_BILLING_PREVIEW_ENDPOINT = "order/sku/billingdate";

export interface ProvisionBillingPreviewRequest {
  provisionCompletionDate: string;
  skuType: string;
  costInterval: string;
  orderId: number;
  skuId: string;
}

export type ProvisionBillingPreview =
  | {
      billingDisplayType: "period";
      firstBillingPeriodStartDate: string;
      firstBillingPeriodEndDate: string;
    }
  | {
      billingDisplayType: "date";
      billingDate: string;
    };

interface ProvisionBillingPreviewData {
  OrderId?: number;
  skuId?: string;
  Date?: string;
  FromDate?: string;
  ToDate?: string;
}

interface ProvisionBillingPreviewResponse {
  Code: number;
  Status: string;
  Data?: ProvisionBillingPreviewData;
  Message?: string;
}

interface ProvisionBillingPreviewErrorResponse {
  message?: string;
  Message?: string;
  Data?: string[];
}

interface RejectValue {
  message: string;
}

export interface ProvisionBillingPreviewState {
  status: "idle" | "loading" | "succeeded" | "failed";
  data: ProvisionBillingPreview | null;
  error: string | null;
  requestedDate: string | null;
  activeRequestId: string | null;
}

const initialState: ProvisionBillingPreviewState = {
  status: "idle",
  data: null,
  error: null,
  requestedDate: null,
  activeRequestId: null,
};

const PreviewError =
  "An error occurred while fetching billing dates. Please try again.";

const handleError = (error: unknown): string => {
  if (!(error instanceof AxiosError)) {
    return PreviewError;
  }

  const data = error.response?.data as ProvisionBillingPreviewErrorResponse;
  if (data?.message) return data.message;
  if (data?.Data?.length) return data.Data.join(", ");
  if (data?.Message) return data.Message;
  return PreviewError;
};

const toPreview = (
  data: ProvisionBillingPreviewData,
  costInterval: string,
): ProvisionBillingPreview => {
  const isOneTime =
    costInterval.replace(/[-_\s]/g, "").toLowerCase() === "onetime";

  if (isOneTime) {
    if (!data.Date) {
      throw new Error(PreviewError);
    }

    return {
      billingDisplayType: "date",
      billingDate: data.Date,
    };
  }

  if (!data.FromDate || !data.ToDate) {
    throw new Error(PreviewError);
  }

  return {
    billingDisplayType: "period",
    firstBillingPeriodStartDate: data.FromDate,
    firstBillingPeriodEndDate: data.ToDate,
  };
};

export const fetchProvisionBillingPreview = createAsyncThunk<
  ProvisionBillingPreview,
  ProvisionBillingPreviewRequest,
  { rejectValue: RejectValue }
>(
  "ProvisionBillingPreview/fetch",
  async (request, { rejectWithValue, signal }) => {
    try {
      const response = await httpClient.post<ProvisionBillingPreviewResponse>(
        PROVISION_BILLING_PREVIEW_ENDPOINT,
        request,
        { signal },
      );
      if (response.status !== 200 || response.data?.Status === "error") {
        return rejectWithValue({
          message: response.data?.Message || PreviewError,
        });
      }
      return toPreview(
        response.data.Data as ProvisionBillingPreviewData,
        request.costInterval,
      );
    } catch (error) {
      return rejectWithValue({
        message: handleError(error),
      });
    }
  },
);

const provisionBillingPreviewSlice = createSlice({
  name: "ProvisionBillingPreview",
  initialState,
  reducers: {
    resetProvisionBillingPreview: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProvisionBillingPreview.pending, (state, action) => {
        state.status = "loading";
        state.data = null;
        state.error = null;
        state.requestedDate = action.meta.arg.provisionCompletionDate;
        state.activeRequestId = action.meta.requestId;
      })
      .addCase(fetchProvisionBillingPreview.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.status = "succeeded";
        state.data = action.payload;
        state.error = null;
        state.activeRequestId = null;
      })
      .addCase(fetchProvisionBillingPreview.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;

        state.status = "failed";
        state.data = null;
        state.error = action.payload?.message || PreviewError;
        state.activeRequestId = null;
      });
  },
});

export const { resetProvisionBillingPreview } =
  provisionBillingPreviewSlice.actions;

export default provisionBillingPreviewSlice.reducer;
